import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Sparkles, Loader2 } from 'lucide-react';
import { SearchResults, flattened, identityKey, previewResults } from './SearchResults';
import { useDiscoverySearch, journey } from './useDiscoverySearch';
import { CoverageNotices } from './CoverageNotices';
import './discovery.css';

export const GlobalSearch = ({scopeKey = ''}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const {data, loading, error, retry} = useDiscoverySearch(query, '', {scopeKey, limit:12});
  const preview = previewResults(data);
  const rows = flattened(preview);
  const selected = rows.findIndex(row => identityKey(row) === selectedKey);
  const navigate = useNavigate();
  const root = useRef();
  const began = useRef(performance.now());
  useEffect(() => {
    const close = e => {if (!root.current?.contains(e.target)) setOpen(false);};
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);
  useEffect(() => {if (selected >= 0) root.current?.querySelector(`[id="header-search-option-${selected}"]`)?.scrollIntoView?.({block:'nearest'});}, [selected]);
  const go = url => {setOpen(false); navigate(url);};
  const choose = row => {
    journey('result_selected', {category:row.entity_type, match_type:row.match_type, duration_ms:Math.round(performance.now()-began.current)});
    journey('report_opened', {category:row.entity_type}); go(row.route);
  };
  const submit = () => {
    if (query.trim().length < 2) return;
    journey('search_submitted', {status:data?.status || (error ? 'service_failure' : 'pending')});
    if (!loading && !error && data?.auto_open && !/\?|\b(what|why|how|who|which|compare)\b/i.test(query)) choose(data.auto_open);
    else go(`/search?q=${encodeURIComponent(query.trim())}`);
  };
  return <div ref={root} className="global-discovery" data-testid="omni-search">
    <div className="discovery-input-row"><Search size={17}/>
      <input data-testid="omni-search-input" aria-label="Search companies, drugs, and markets" role="combobox" aria-expanded={open}
        aria-controls="header-search-list" aria-autocomplete="list" aria-activedescendant={selected >= 0 ? `header-search-option-${selected}` : undefined}
        value={query} placeholder="Find a company, drug, or market" onFocus={() => setOpen(true)}
        onChange={e => {setQuery(e.target.value); setOpen(true); setSelectedKey(null); began.current=performance.now();}}
        onKeyDown={e => {
          if (e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229) return;
          if (e.key === 'ArrowDown') {e.preventDefault(); setOpen(true); const next = rows[Math.min(rows.length-1, selected+1)]; setSelectedKey(next ? identityKey(next) : null);}
          if (e.key === 'ArrowUp') {e.preventDefault(); const next = rows[selected-1]; setSelectedKey(next ? identityKey(next) : null);}
          if (e.key === 'Escape') {setOpen(false); setSelectedKey(null);}
          if (e.key === 'Enter') {e.preventDefault(); selected >= 0 ? choose(rows[selected]) : submit();}
        }}/>
      {query && <button type="button" data-testid="search-clear-button" aria-label="Clear search" onClick={() => {setQuery(''); setSelectedKey(null);}}><X size={15}/></button>}
      <button type="button" data-testid="search-btn" aria-label="Search" disabled={query.trim().length < 2} onClick={submit}>{loading ? <Loader2 className="animate-spin" size={16}/> : <Search size={16}/>}<span>Search</span></button>
    </div>
    {open && query.trim().length >= 2 && <div className="discovery-dropdown" data-testid="search-dropdown">
      <div role="status" data-testid="search-status" className="discovery-status">
        {loading ? 'Looking up saved company, drug and market identities…' : error || (data?.status === 'partial' ? 'Some catalog sources are unavailable; available matches are shown.' : data?.status === 'not_in_catalog' ? `No saved match for “${query}”.` : data?.status === 'correction' ? 'Spelling suggestions—choose the intended identity.' : 'Saved catalog matches')}
      </div>
      {(error || data?.status === 'partial') && <button data-testid="search-retry-button" className="discovery-footer" onClick={retry}>Retry catalog lookup</button>}
      <SearchResults data={preview} selected={selected} onSelect={choose} prefix="header-search" optionList compact/>
      <CoverageNotices items={data?.coverage_notices?.slice(0,2)} prefix="header-search" onNavigate={go}/>
      <button data-testid="search-browse-results" className="discovery-footer" onClick={() => go(`/search?q=${encodeURIComponent(query.trim())}`)}><Search size={16}/>View all results and check connected sources</button>
      <button data-testid="search-ask-ai" className="discovery-footer" onClick={() => go(`/ask?q=${encodeURIComponent(query.trim())}&draft=1`)}><Sparkles size={16}/>Ask AI about “{query}” · review question</button>
      {data?.status === 'not_in_catalog' && <button data-testid="search-company-coverage-request" className="discovery-footer" onClick={() => go(`/company-coverage?q=${encodeURIComponent(query.trim())}`)}>Request company coverage</button>}
    </div>}
  </div>;
};
